/**
 * Premium-data gating for the "Load from prefilled data" preset catalogue (Lipper-to-Sphere,
 * MDOT-24810). Two customer licenses gate the premium presets, both read from
 * GET /v3/customers/{id}: `fundDataAccessTier` (Lipper funds data) and `bondDataAccessTier`
 * (SNB bonds data). A license counts only when it is explicitly PREMIUM — BASE and an
 * unreported/undefined tier both read as unlicensed.
 *
 * - Funds-sourced presets (Sector, Dividend Yield, Distribution Yield, Effective Maturity) need
 *   the funds license; without it the option is disabled and labelled "(Premium data)". The bond
 *   license does not unlock them.
 * - Dual-sourced presets (Duration, YTM) have data from both providers: either license enables
 *   them, and only both missing disables them (same tag). Under exactly one license the option is
 *   enabled and untagged, and the side panel shows an informative banner on selection saying which
 *   instrument family the prefilled data will cover — see `dualSourceCoverage`.
 */
import type { StandardClassificationId, StandardInstrumentsClassificationDto } from "@eu.mdotm.api/sphere";
import { BondDataAccessTier, FundDataAccessTier } from "@eu.mdotm.api/sphere";
import type { SelectOption } from "@mdotm/shadcn/mdotm/components";

export const PREMIUM_DATA_LABEL_SUFFIX = " (Premium data)";

/** The funds-scoped premium (Lipper) preset columns — the funds license alone gates these. */
const fundsPremiumClassificationIds: ReadonlySet<string> = new Set([
	"DIVIDEND_YIELD",
	"DISTRIBUTION_YIELD",
	"EFFECTIVE_MATURITY",
	"SECTOR",
] satisfies Array<StandardClassificationId>);

/** The dual-sourced premium preset columns — either the funds or the bonds license enables these. */
const dualSourceClassificationIds: ReadonlySet<string> = new Set([
	"DURATION",
	"YTM",
] satisfies Array<StandardClassificationId>);

/** The two premium-data licenses, structurally satisfied by `CustomerDetailsDTO`. */
export type CustomerDataAccessTiers = {
	fundDataAccessTier?: FundDataAccessTier;
	bondDataAccessTier?: BondDataAccessTier;
};

export function hasFundsLicense(tiers: CustomerDataAccessTiers): boolean {
	return tiers.fundDataAccessTier === FundDataAccessTier.Premium;
}

function hasBondsLicense(tiers: CustomerDataAccessTiers): boolean {
	return tiers.bondDataAccessTier === BondDataAccessTier.Premium;
}

function isLockedClassification(standardClassificationId: string | undefined, tiers: CustomerDataAccessTiers): boolean {
	if (standardClassificationId === undefined) {
		return false;
	}
	if (fundsPremiumClassificationIds.has(standardClassificationId)) {
		return !hasFundsLicense(tiers);
	}
	if (dualSourceClassificationIds.has(standardClassificationId)) {
		return !hasFundsLicense(tiers) && !hasBondsLicense(tiers);
	}
	return false;
}

export function presetOptionFromClassification(
	classification: StandardInstrumentsClassificationDto,
	tiers: CustomerDataAccessTiers,
): SelectOption<StandardClassificationId> {
	const name = classification.name ?? "";
	const locked = isLockedClassification(classification.standardClassificationId, tiers);
	return {
		// The suffix rides on the label so a premium option is still findable by its column name
		// (search runs on the label). The tag and `disabled` always move together: an enabled
		// option never carries it.
		label: locked ? `${name}${PREMIUM_DATA_LABEL_SUFFIX}` : name,
		value: classification.standardClassificationId!,
		disabled: locked || undefined,
	};
}

export type DualSourceCoverage = "funds-only" | "bonds-only";

/**
 * For a dual-sourced preset (Duration, YTM) selected under exactly one of the two licenses, says
 * which instrument family the prefilled data will cover; `undefined` (no banner) for full or no
 * coverage — a no-license customer never gets here since the option is disabled — and for every
 * other preset.
 */
export function dualSourceCoverage(
	standardClassificationId: string | null | undefined,
	tiers: CustomerDataAccessTiers,
): DualSourceCoverage | undefined {
	if (standardClassificationId == null || !dualSourceClassificationIds.has(standardClassificationId)) {
		return undefined;
	}
	const funds = hasFundsLicense(tiers);
	const bonds = hasBondsLicense(tiers);
	if (funds && !bonds) {
		return "funds-only";
	}
	if (bonds && !funds) {
		return "bonds-only";
	}
	return undefined;
}

export const DUAL_SOURCE_COVERAGE_BANNER: Record<DualSourceCoverage, string> = {
	"funds-only": "This column is available for funds only. Bond data requires a premium plan.",
	"bonds-only": "This column is available for bonds only. Fund data requires a premium plan.",
};
